import {TabProps, TabsHeader} from 'platform/components';
import {HStack, Scroll, Show, Space} from 'platform/foundation';

import {useRef} from 'react';
import {useSelector} from 'react-redux';
import {useLocation, useParams} from 'react-router-dom';

import {isNotNil, isNilOrEmpty} from 'ramda-adjunct';

import {selectSaleVehicle, useGetRentalVehicleQuery} from '@dms/api/vehicle';
import {useGetServiceVehicleQuery} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {
  ServiceCasePhotos,
  useAdPlatformsVehicleUpdateDialog,
  usePermissions,
  useVehiclePhotosPermissions,
} from '@dms/shared';
import {Inspections, VehiclePhotosRoutes, useRouter} from '@dms/teas';

import {buildArray, composePath, useRequiredParams} from 'shared/web';

import {VehiclePhotoGallery} from '../../../../../components/VehiclePhotoGallery/VehiclePhotoGallery';
import {VehicleCreateAlbum} from '../VehicleCreateAlbum';
import {BatchDownloadButton} from './BatchDownloadButton';
import {ConditionPhotos} from './ConditionPhotos';
import {InspectionPhotos} from './InspectionPhotos';

export const Photos = () => {
  const router = useRouter();
  const {id: vehicleId} = useRequiredParams();
  const {tab, inspectionId} = useParams();

  const location = useLocation();
  const isUpdatingVehicleAds = useRef(false);

  const [canReadServiceCase] = usePermissions({permissionKeys: ['serviceCaseRead']});

  const {canReadVehicleAlbumPhotos} = useVehiclePhotosPermissions({
    vehicleId,
  });

  const [updateVehicleAds] = useAdPlatformsVehicleUpdateDialog();
  const {data: saleVehicle} = useSelector(selectSaleVehicle(vehicleId));
  const {data: serviceVehicle} = useGetServiceVehicleQuery(
    {
      vehicleId,
    },
    {
      skip: isNilOrEmpty(vehicleId),
    }
  );
  const {data: rentalVehicle} = useGetRentalVehicleQuery(
    {
      vehicleId,
    },
    {
      skip: isNilOrEmpty(vehicleId),
    }
  );
  const isSaleVehicle = isNotNil(saleVehicle);
  const isServiceVehicle = isNotNil(serviceVehicle);
  const isRentalVehicle = isNotNil(rentalVehicle);

  const onVehiclePhotosUpdate = () => {
    if (isUpdatingVehicleAds.current) {
      return;
    }
    isUpdatingVehicleAds.current = true;
    updateVehicleAds(vehicleId).finally(() => {
      isUpdatingVehicleAds.current = false;
    });
  };

  if (inspectionId) {
    return <InspectionPhotos />;
  }

  const handleChangeTab = (tab: string) => {
    router.push(
      composePath(vehiclesRoutes.photos, {
        params: {
          id: vehicleId,
          tab,
        },
      })
    );
  };

  const tabs = buildArray<TabProps>()
    .when(canReadVehicleAlbumPhotos && isSaleVehicle, {
      title: i18n.t('entity.vehicle.labels.vehiclePhotos'),
      id: VehiclePhotosRoutes.vehiclePhotos,
      'data-testid': testIds.vehicles.photos(VehiclePhotosRoutes.vehiclePhotos),
      content: <VehiclePhotoGallery onVehiclePhotosUpdate={onVehiclePhotosUpdate} />,
    })
    .when(isSaleVehicle || isRentalVehicle, {
      title: i18n.t('entity.vehicle.labels.vehicleCondition'),
      id: VehiclePhotosRoutes.condition,
      'data-testid': testIds.vehicles.photos(VehiclePhotosRoutes.condition),
      content: <ConditionPhotos />,
    })
    .when(isSaleVehicle || isRentalVehicle || isServiceVehicle, {
      title: i18n.t('entity.vehicle.labels.inspections'),
      id: VehiclePhotosRoutes.inspections,
      'data-testid': testIds.vehicles.photos(VehiclePhotosRoutes.inspections),
      content: (
        <Inspections
          data-testid={testIds.vehicles.photos('photoInspection')}
          getRedirectRoute={(_id, _inspectionId) =>
            composePath(vehiclesRoutes.photoByInspectionId, {
              params: {
                id: _id,
                inspectionId: _inspectionId,
                tab: VehiclePhotosRoutes.inspections,
              },
            })
          }
        />
      ),
    })
    .when(canReadServiceCase, {
      title: i18n.t('entity.vehicle.labels.serviceCasePhotos'),
      id: VehiclePhotosRoutes.serviceCase,
      'data-testid': testIds.vehicles.photos(VehiclePhotosRoutes.serviceCase),
      content: <ServiceCasePhotos vehicleId={vehicleId} />,
    });

  return (
    <>
      <HStack justify="space-between" width="100%" spacing={4} wrap>
        <Scroll auto>
          <TabsHeader
            variant="condensed"
            onChange={handleChangeTab}
            activeTabId={tab}
            tabs={tabs}
          />
        </Scroll>
        <Show
          when={location.pathname.includes(VehiclePhotosRoutes.vehiclePhotos) && isSaleVehicle}
          whenFeatureEnabled={featureFlags.VEHICLE_MULTIPLE_ALBUMS}
        >
          <HStack spacing={2} justify="flex-end" grow={1}>
            <BatchDownloadButton saleVehicleId={saleVehicle?.id} />
            <VehicleCreateAlbum vehicleId={vehicleId} saleVehicleId={saleVehicle?.id} />
          </HStack>
        </Show>
      </HStack>

      <Space vertical={4} />
      {(tabs.find((item) => item.id === tab) ?? tabs[0])?.content}
    </>
  );
};
